import {
  CYPHER_WRITE_BLOCKED_LABELS,
  NODE_PROPERTY_SCHEMAS,
  RELATIONSHIP_POLICY,
  RELATIONSHIP_PROPERTY_SCHEMAS,
  type NodeLabel,
  type NodePropertySchema,
  type RelationshipPolicy,
  type RelationshipPropertySchema,
  type RelationshipType
} from '@atbash-labs/ontology-core'

/**
 * The set of node labels and relationship types that the live ontology
 * publishes, plus the cypher-write enforcement policy (per-relationship
 * endpoint constraints, per-label CREATE/MERGE opt-outs, and per-label
 * property schemas).
 *
 * The validator rejects any `:Label` or `[:REL]` token in the candidate
 * cypher that is NOT a member of the relevant set, regardless of whether it
 * appears in a CREATE / MERGE / MATCH context. It additionally rejects:
 *
 *   - relationship patterns inside CREATE clauses (relationship writes must
 *     use MERGE so an existing edge is never duplicated);
 *   - relationship patterns whose source/target labels do not match any
 *     `(source, target)` pair declared in `relationshipPolicy[type].endpoints`
 *     (skipped when `relationshipPolicy[type].unconstrained` is true);
 *   - CREATE / MERGE clauses that introduce a node with a label in
 *     `cypherWriteBlockedLabels` (read-only MATCH patterns are still allowed);
 *   - node patterns (inline maps in `(n:Label {key: value})`) and SET items
 *     (`SET n.key = value`) whose property key is declared in the label's
 *     `nodePropertySchemas` entry but whose literal value is the wrong
 *     primitive type or violates the property's enum;
 *   - relationship patterns (inline maps in `(a)-[:R {key: value}]->(b)`)
 *     and SET items on rel variables (`SET r.key = value`) whose key is
 *     declared in the rel's `relationshipPropertySchemas` entry but whose
 *     literal value is the wrong primitive type or violates the enum.
 *     Unknown keys are permitted on both nodes and rels so agents can
 *     attach ad-hoc metadata; only declared keys with wrong values fail.
 *
 * Either pass the static `NODE_LABELS` / `RELATIONSHIP_TYPES` /
 * `RELATIONSHIP_POLICY` / `CYPHER_WRITE_BLOCKED_LABELS` /
 * `NODE_PROPERTY_SCHEMAS` exports from `@atbash-labs/ontology-core` directly,
 * or pass a snapshot fetched at runtime from `GET /api/v1/ontology/schema`
 * for environments where the graph has been narrowed (e.g. a branch) and
 * only a subset of labels is allowed.
 */
export interface SchemaSnapshot {
  labels: ReadonlySet<string>
  relationshipTypes: ReadonlySet<string>
  relationshipPolicy: ReadonlyMap<string, RelationshipPolicy>
  cypherWriteBlockedLabels: ReadonlySet<string>
  nodePropertySchemas: ReadonlyMap<string, NodePropertySchema>
  relationshipPropertySchemas: ReadonlyMap<string, RelationshipPropertySchema>
}

/**
 * Source location of a violation in the candidate cypher. Lines are
 * 1-based; columns are 0-based to match the language-support library.
 * `offsets` are absolute character offsets from the start of the query
 * suitable for slicing the original string.
 */
export interface ValidationPosition {
  line: number
  column: number
  offsets: { start: number; end: number }
}

export type ValidationErrorKind =
  | 'parse_error'
  | 'unknown_label'
  | 'unknown_relationship_type'
  | 'unknown_label_or_relationship_type'
  | 'empty_query'
  | 'illegal_endpoint'
  | 'unresolvable_endpoint'
  | 'relationship_create_forbidden'
  | 'blocked_label_create'
  | 'invalid_property_type'
  | 'invalid_property_enum'
  | 'blocked_property_write'
  | 'missing_required_property'
  | 'unknown_property_key'
  | 'missing_node_id'
  | 'invalid_node_id'

export interface ValidationError {
  kind: ValidationErrorKind
  /** Human-readable description suitable for surfacing back to the LLM and the user. */
  message: string
  /** The offending token, when available (e.g. the label/relationship type literal). */
  token?: string
  /** Source position when the parser supplied one. */
  position?: ValidationPosition
}

export type ValidationResult =
  | { ok: true; warnings: ValidationError[] }
  | { ok: false; errors: ValidationError[]; warnings: ValidationError[] }

/**
 * Per-caller enforcement toggles. The structural checks (parse errors,
 * unknown label / rel-type membership, URN ids, literal property type +
 * enum, required-property warnings) always run. The two flags below select
 * the policy for raw-Cypher callers and legacy stored write rows.
 *
 * Defaults are `true` (strict policy = transform LLM behaviour).
 * {@link QUERY_AGENT_CYPHER_WRITE_POLICY} preserves the historical relaxed
 * raw-Cypher profile. Active `propose_cypher_writes` structured envelopes do
 * not use that profile: graph validation enforces blocked upsert labels and
 * relationship endpoints at both proposal and apply.
 */
export interface CypherWriteValidationPolicy {
  /**
   * When `true` (default), CREATE / MERGE on any label in
   * `cypherWriteBlockedLabels` is a `blocked_label_create` error. When
   * `false`, the check is skipped so the agent may propose writes against
   * system / ingest labels.
   */
  enforceBlockedLabels?: boolean
  /**
   * When `true` (default), constrained relationship patterns inside CREATE /
   * MERGE are checked against `relationshipPolicy[type].endpoints` and
   * report `illegal_endpoint` / `unresolvable_endpoint` errors. When
   * `false`, both checks are skipped.
   */
  enforceRelationshipEndpoints?: boolean
}

/**
 * Strict policy used by the zip-ingest transform LLM and the ingest agent's
 * post-ingest cypher proposals. Enforces every cypher-write rule the
 * ontology publishes.
 */
export const STRICT_CYPHER_WRITE_POLICY: CypherWriteValidationPolicy = {
  enforceBlockedLabels: true,
  enforceRelationshipEndpoints: true
}

/**
 * Historical raw-Cypher profile for legacy query-agent rows. It skips the
 * blocked-label CREATE/MERGE and constrained-endpoint checks while retaining
 * structural checks. It does not govern active structured graph proposals;
 * those use the strict graph-envelope validators at proposal and apply.
 */
export const QUERY_AGENT_CYPHER_WRITE_POLICY: CypherWriteValidationPolicy = {
  enforceBlockedLabels: false,
  enforceRelationshipEndpoints: false
}

/**
 * Optional inputs for {@link validateCypherWrite}. When `params` is supplied,
 * string parameter values bound to `id` on CREATE / MERGE node patterns are
 * checked against the canonical URN scheme for the node label.
 *
 * `policy` selects the enforcement profile; defaults to
 * {@link STRICT_CYPHER_WRITE_POLICY} for backwards compatibility with the
 * transform LLM and existing tests.
 */
export interface ValidateCypherWriteOptions {
  params?: Record<string, unknown>
  policy?: CypherWriteValidationPolicy
}

/**
 * Optional overrides for {@link buildSchemaSnapshot}. When omitted, the
 * defaults pulled from `@atbash-labs/ontology-core` are used (the canonical
 * compile-time policy table, blocked-labels set, and per-label property
 * schemas). Callers that want to narrow any of these at runtime (e.g. a
 * branch with a custom block-list or a restricted property schema) can
 * supply their own.
 */
export interface SchemaSnapshotOverrides {
  relationshipPolicy?: ReadonlyMap<string, RelationshipPolicy>
  cypherWriteBlockedLabels?: ReadonlySet<string>
  nodePropertySchemas?: ReadonlyMap<string, NodePropertySchema>
  relationshipPropertySchemas?: ReadonlyMap<string, RelationshipPropertySchema>
}

/**
 * Builds a {@link SchemaSnapshot} from iterables of labels and relationship
 * types, defaulting the cypher-write policy, blocked-labels set, and
 * per-label property schemas to the canonical generated tables in
 * `@atbash-labs/ontology-core`. Pass `overrides` to substitute a narrower
 * policy, block-list, or property-schema table for branch / preview
 * environments.
 */
export function buildSchemaSnapshot(
  labels: Iterable<NodeLabel | string>,
  relationshipTypes: Iterable<RelationshipType | string>,
  overrides: SchemaSnapshotOverrides = {}
): SchemaSnapshot {
  const policy =
    overrides.relationshipPolicy ??
    new Map<string, RelationshipPolicy>(Object.entries(RELATIONSHIP_POLICY))
  const blocked = overrides.cypherWriteBlockedLabels ?? CYPHER_WRITE_BLOCKED_LABELS
  const propertySchemas =
    overrides.nodePropertySchemas ??
    new Map<string, NodePropertySchema>(Object.entries(NODE_PROPERTY_SCHEMAS))
  const relPropertySchemas =
    overrides.relationshipPropertySchemas ??
    new Map<string, RelationshipPropertySchema>(Object.entries(RELATIONSHIP_PROPERTY_SCHEMAS))
  return {
    labels: new Set(labels),
    relationshipTypes: new Set(relationshipTypes),
    relationshipPolicy: policy,
    cypherWriteBlockedLabels: blocked,
    nodePropertySchemas: propertySchemas,
    relationshipPropertySchemas: relPropertySchemas
  }
}
